/* ═══════════════════════════════════════════════════════════════
   The app-standard zoom control (docs/COHESION_R1_PLAN.md Phase 2/4,
   "Zoom (law)"): magnifier-minus, a percent readout that resets to 100%
   on click, magnifier-plus. Visual base = the forma-studio zoom control
   (forma-studio/Studio.tsx) — same `dsg-group` / `dsg-icon-btn` /
   `dsg-zoom-value` chrome every dsg-family surface already uses
   (editor.css, semantic tokens throughout), so every zoomable surface
   reads as ONE control. Every caller must already import editor.css —
   true today of forma-studio, spec-studio and the project Designer.

   Range 50–180% everywhere (law). `value`/`onChange` are PERCENT
   (100 = actual size), not the 0–1 fraction some callers store
   internally — convert at the call site.
   ═══════════════════════════════════════════════════════════════ */

import * as React from 'react';

export interface ZoomControlProps {
  /** Current zoom, in percent (100 = actual size). */
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** Percent moved per click on a magnifier button. */
  step?: number;
}

export function ZoomControl({ value, onChange, min = 50, max = 180, step = 10 }: ZoomControlProps) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  return (
    <div className="dsg-group" role="group" aria-label="Zoom">
      <button
        type="button"
        className="dsg-icon-btn"
        aria-label="Zoom out"
        title="Zoom out"
        disabled={value <= min}
        onClick={() => onChange(clamp(value - step))}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M8 11h6M20 20l-3.5-3.5" />
        </svg>
      </button>
      <button
        type="button"
        className="dsg-zoom-value"
        onClick={() => onChange(100)}
        title="Reset zoom to 100%"
      >
        {Math.round(value)}%
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        aria-label="Zoom in"
        title="Zoom in"
        disabled={value >= max}
        onClick={() => onChange(clamp(value + step))}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M8 11h6M11 8v6M20 20l-3.5-3.5" />
        </svg>
      </button>
    </div>
  );
}

export default ZoomControl;
